// The eyes behind the glass, read from the Aseprite sheets (client/art/aseprite/eyes/, built by art/aseprite/src/eyes.lua).
// Nothing is drawn here: a sprite is cut out of the exported PNG by the JSON's frames and slices. Palette index 0 is
// clear, 1 ink, 2 paper, 3 red, 4 green. Two sizes: L (plain visitors, gold and red knocks) and S (beasts, lit knocks, costume holes).
const FPS = 15;

const BASE = new URL('./art/aseprite/eyes/', import.meta.url);

// ---- the sheet: tags by name, one sprite per frame and side ----
export function parseSheet({ w, index, json }) {
  const { frames, meta } = json;
  const slice = n => meta.slices.find(s => s.name === n);
  const sides = [slice('eye-left'), slice('eye-right')];
  const tags = Object.fromEntries(meta.frameTags.map(t => [t.name, { from: t.from, to: t.to }]));
  const cache = new Map();
  // the frame's own sprite for side 0 (left) or 1 (right): { w, h, px, py (the eye point), idx }
  function sprite(f, side) {
    const key = f * 2 + side;
    if (cache.has(key)) return cache.get(key);
    const { x: fx, y: fy } = frames[f].frame, { bounds: b, pivot } = sides[side].keys[0];
    const idx = new Uint8Array(b.w * b.h);
    for (let y = 0; y < b.h; y++) for (let x = 0; x < b.w; x++) idx[y * b.w + x] = index[(fy + b.y + y) * w + fx + b.x + x];
    const s = { w: b.w, h: b.h, px: pivot.x, py: pivot.y, idx };
    cache.set(key, s);
    return s;
  }
  // a tag's frames at 15 fps, one entry per game frame: the blink's half-shut, shut, shut, half-shut
  const steps = name => {
    const t = tags[name], out = [];
    for (let f = t.from; f <= t.to; f++) for (let k = Math.max(1, Math.round(frames[f].duration * FPS / 1000)); k > 0; k--) out.push(f);
    return out;
  };
  return { tags, sprite, steps, count: frames.length };
}

// ---- PNG (8-bit indexed, not interlaced) ----
async function inflate(bytes) {
  const s = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(s).arrayBuffer());
}
export async function decodeIndexed(buf) {
  const v = new DataView(buf.buffer, buf.byteOffset, buf.byteLength), parts = [];
  let w = 0, h = 0;
  for (let p = 8; p < buf.length;) {
    const len = v.getUint32(p), type = String.fromCharCode(...buf.subarray(p + 4, p + 8));
    if (type === 'IHDR') { w = v.getUint32(p + 8); h = v.getUint32(p + 12); if (buf[p + 16] !== 8 || buf[p + 17] !== 3 || buf[p + 20]) throw new Error('eye sheet: 8-bit indexed, not interlaced'); }
    if (type === 'IDAT') parts.push(buf.subarray(p + 8, p + 8 + len));
    p += 12 + len;
  }
  const comp = new Uint8Array(parts.reduce((n, a) => n + a.length, 0));
  parts.reduce((o, a) => (comp.set(a, o), o + a.length), 0);
  const raw = await inflate(comp), out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    const f = raw[y * (w + 1)], src = y * (w + 1) + 1, dst = y * w;
    for (let i = 0; i < w; i++) {
      const a = i ? out[dst + i - 1] : 0, b = y ? out[dst - w + i] : 0, c = y && i ? out[dst - w + i - 1] : 0;
      const pa = Math.abs(b - c), pb = Math.abs(a - c), pc = Math.abs(a + b - 2 * c);
      out[dst + i] = (raw[src + i] + [0, a, b, (a + b) >> 1, pa <= pb && pa <= pc ? a : pb <= pc ? b : c][f]) & 255;
    }
  }
  return { w, h, index: out };
}

const isNode = !!globalThis.process?.versions?.node;
async function bytes(url) {
  if (isNode) { const fs = 'node:fs/promises'; return new Uint8Array(await (await import(fs)).readFile(url)); } // a name, not a literal: the page never asks for it
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return new Uint8Array(await r.arrayBuffer());
}
export async function loadSheet(size) {
  const png = await decodeIndexed(await bytes(new URL(`eyes-${size}-sheet.png`, BASE)));
  const json = JSON.parse(new TextDecoder().decode(await bytes(new URL(`eyes-${size}-sheet.json`, BASE))));
  return parseSheet({ ...png, json });
}

// The sheets the game draws from. A missing sheet (offline, a 404) leaves the glass without eyes, never with redrawn ones.
const sheets = { L: null, S: null };
await Promise.all(['L', 'S'].map(async z => { try { sheets[z] = await loadSheet(z); } catch (e) { console.warn('eyes:', e.message); } }));
export const eyeSheet = size => sheets[size];
export const setEyeSheet = (size, sheet) => { sheets[size] = sheet; };

// ---- which frame: UI events and a seed, never the kind ----
const hash = (a, b) => { let h = (a * 374761393 + b * 668265263) | 0; h = Math.imul(h ^ (h >>> 13), 1274126177); return ((h ^ (h >>> 16)) >>> 0) / 4294967296; };
const BLINK_GAP = [45, 75]; // frames between blinks: 3 to 5 s
const starts = new Map();
// the game frame (0 = the first) a seed's k-th blink starts at
function blinkStart(seed, k) {
  const list = starts.get(seed) ?? starts.set(seed, []).get(seed);
  while (list.length <= k) list.push((list.at(-1) ?? 0) + BLINK_GAP[0] + Math.floor(hash(seed, list.length) * (BLINK_GAP[1] - BLINK_GAP[0] + 1)));
  if (starts.size > 64) starts.clear();
  return list[k];
}
// -1, or how many frames into a blink this frame is
export function blinkAt(seed, frame, len = 4) {
  for (let k = 0; ; k++) { const s = blinkStart(seed, k); if (s > frame) return -1; if (frame < s + len) return frame - s; }
}
// Gaze: idle (the chat, down-right), you (the player is typing), open (Open is hovered or focused), think (the visitor is typing).
// One stepped frame changes it. Reduced motion: idle, no blinks. Returns { tag, i } (i: the step inside the tag).
export function eyeTrack({ seed = 0, frame = 0, you = false, open = false, think = false, reduced = false } = {}) {
  if (reduced) return { tag: 'idle', i: 0 };
  const b = blinkAt(seed, frame, sheets.L?.steps('blink').length);
  if (b >= 0) return { tag: 'blink', i: b };
  return { tag: open ? 'open' : think ? 'think' : you ? 'you' : 'idle', i: 0 };
}

// Is the player's hand on a button (the `open` gaze)? A keyboard focus ring, a press, or a mouse over it. On a touch screen
// :hover and the focus a tap leaves behind both stick after the finger lifts, so there only a press counts (sprint 32 note).
export function handOn(el, canHover = true) {
  if (!el || el.disabled || typeof el.matches !== 'function') return false;
  const is = q => { try { return !!el.matches(q); } catch { return false; } };
  return is(':active') || is(':focus-visible') || (canHover && is(':hover'));
}
export const canHover = (doc = globalThis.document) => { try { return doc?.defaultView?.matchMedia?.('(hover: hover)')?.matches ?? true; } catch { return true; } };

// The look at the reveal wins over the gaze: the alien's red eyes, the infected's green, a human's happy arcs
// (a human with a ringed slip: the worried lids). look: 'alien' | 'infected' | 'human' | null.
export const eyeLook = (red, sick, kind, revealT) => red ? 'alien' : sick ? 'infected' : kind === 'human' && revealT >= 0 ? 'human' : null;
export function pickEye(size, eye, look = null, slip = false) {
  if (look === 'alien' || look === 'infected') return { size, tag: look, i: 0 };
  if (look === 'human') return { size, tag: slip ? 'slip' : 'human', i: 0 };
  return { size, tag: eye?.tag ?? 'idle', i: eye?.i ?? 0 };
}

// The sprites to paint for an eye choice at the eye points: [{ s: sprite, x, y }] with the box's top-left on the glass.
export function eyeStamps(eye, points) {
  const sheet = sheets[eye.size];
  if (!sheet) return [];
  const f = sheet.steps(eye.tag)[eye.i] ?? sheet.tags[eye.tag].from;
  return points.map(([ex, ey], n) => { const s = sheet.sprite(f, n & 1); return { s, x: Math.round(ex) - s.px, y: Math.round(ey) - s.py }; });
}
